import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, UserPlus } from "lucide-react";
import { api, formatApiError } from "../api";
import { ORG_STAFF } from "../constants/testIds";
import { useAppData } from "../contexts/AppDataContext";
import { fadeUp } from "../lib/motion";
import { useSortPersistence } from "../hooks/useSortPersistence";
import { Button } from "../components/ui/button";
import { StatusPill } from "../components/common/StatusPill";
import { GymLoader } from "../components/common/GymLoader";
import { ErrorState } from "../components/common/ErrorState";
import { SuspendDialog } from "../components/common/SuspendDialog";
import { SortControl } from "../components/common/SortControl";
import { toast } from "../components/ui/sonner";
import { StaffFormDialog } from "../components/staff/StaffFormDialog";

const TABS = [{ key: "staff", label: "Staff" }, { key: "roles", label: "Roles & permissions" }];
const SORT_FIELDS = [{ value: "id", label: "Newest first", type: "date" }, { value: "name", label: "Name (A–Z)", type: "alpha" }];

// UX-GAP-01 §1.6 — Owner Cockpit "Staff": was a StubPage. Two tabs: the real staff roster
// (add/edit/deactivate, all against the pre-existing `/staff` CRUD) and a read-only "Roles &
// permissions" view of `roles_config.DEFAULT_ROLE_PERMISSIONS` via the 2 new endpoints this
// session added (`/staff/seats`, `/staff/roles`) — see their own comments in `routers/staff.py`
// for why they're additive, read-only surfacing of data that already existed.
export default function Staff() {
  const { branches } = useAppData();
  const reducedMotion = useReducedMotion();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState("staff");
  const [formOpen, setFormOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [suspendTarget, setSuspendTarget] = useState(null);

  const seatsQ = useQuery({ queryKey: ["staff-seats"], queryFn: async () => (await api.get("/staff/seats")).data });
  const { sortBy, sortDir, setSort } = useSortPersistence("staff-list", "id", "desc");
  const staffQ = useQuery({
    queryKey: ["staff-list", sortBy, sortDir],
    queryFn: async () => (await api.get("/staff", { params: { sort_by: sortBy === "id" ? undefined : sortBy, sort_dir: sortDir } })).data.staff,
    enabled: tab === "staff",
  });
  const rolesQ = useQuery({ queryKey: ["staff-roles"], queryFn: async () => (await api.get("/staff/roles")).data.roles });

  const roleLabelByName = useMemo(() => Object.fromEntries((rolesQ.data || []).map((r) => [r.name, r.label])), [rolesQ.data]);
  const branchNameById = useMemo(() => Object.fromEntries(branches.map((b) => [b.id, b.name])), [branches]);

  // DEFECT-D-05: reason-required, immediate-effect suspend/unsuspend — replaces the old plain
  // status-toggle PATCH with dedicated, audited, session-revoking endpoints.
  const suspendMutation = useMutation({
    mutationFn: ({ id, reason }) => api.post(`/staff/${id}/suspend`, { reason }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["staff-list"] }); setSuspendTarget(null); toast.success("Staff member suspended"); },
    onError: (e) => toast.error(formatApiError(e).message),
  });
  const unsuspendMutation = useMutation({
    mutationFn: (id) => api.post(`/staff/${id}/unsuspend`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["staff-list"] }); toast.success("Staff member reinstated"); },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const seatsFull = seatsQ.data && seatsQ.data.seats_used >= seatsQ.data.seat_limit;

  return (
    <div className="space-y-5 max-w-4xl" data-testid={ORG_STAFF.page}>
      <motion.div {...fadeUp(reducedMotion)} className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "var(--scarlet-text)", letterSpacing: "-0.02em" }}>Staff</h1>
          <p className="text-sm mt-0.5" style={{ color: "var(--scarlet-text-secondary)" }}>Who has access, what they can do, and how many seats you have left.</p>
        </div>
        <Button
          data-testid={ORG_STAFF.addButton} disabled={seatsFull}
          onClick={() => { setEditingStaff(null); setFormOpen(true); }}
          className="text-white gap-2" style={{ background: "var(--scarlet-accent)" }}
        >
          <UserPlus size={15} /> Add staff
        </Button>
      </motion.div>

      {seatsQ.data && (
        <motion.div {...fadeUp(reducedMotion, 0.02)} className="solid-surface rounded-[14px] px-5 py-4 flex items-center justify-between" data-testid={ORG_STAFF.seatGauge}>
          <div>
            <p className="eyebrow">Seats used</p>
            <p className="text-xl font-bold mt-0.5" style={{ color: "var(--scarlet-text)" }}>{seatsQ.data.seats_used} <span className="text-sm font-medium" style={{ color: "var(--scarlet-text-secondary)" }}>/ {seatsQ.data.seat_limit}</span></p>
          </div>
          <div className="w-40 h-2 rounded-full overflow-hidden" style={{ background: "var(--scarlet-hairline)" }}>
            <div className="h-full rounded-full" style={{ width: `${Math.min(100, (seatsQ.data.seats_used / Math.max(1, seatsQ.data.seat_limit)) * 100)}%`, background: seatsFull ? "var(--scarlet-neg)" : "var(--scarlet-accent)" }} />
          </div>
        </motion.div>
      )}
      {seatsFull && <p className="text-sm font-medium" data-testid={ORG_STAFF.seatLimitError} style={{ color: "var(--scarlet-neg)" }}>Staff seat limit reached — contact Kairoo to raise your limit before adding more staff.</p>}

      <div className="flex gap-1.5">
        {TABS.map((t) => (
          <button
            key={t.key} data-testid={ORG_STAFF.tab(t.key)} onClick={() => setTab(t.key)}
            className="text-sm font-medium rounded-[10px] px-3.5 py-1.5 transition-colors"
            style={tab === t.key ? { background: "var(--scarlet-accent)", color: "#fff" } : { background: "transparent", border: "1px solid var(--scarlet-hairline)", color: "var(--scarlet-text-secondary)" }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "staff" && (
        <>
          {staffQ.isError && <ErrorState error={staffQ.error} onRetry={() => staffQ.refetch()} />}
          {staffQ.isLoading && <GymLoader label="Loading staff…" />}
          {staffQ.data && staffQ.data.length === 0 && (
            <div className="solid-surface rounded-[14px] p-10 text-center text-sm" data-testid={ORG_STAFF.emptyState} style={{ color: "var(--scarlet-text-secondary)" }}>No staff yet.</div>
          )}
          {staffQ.data && staffQ.data.length > 0 && (
            <div className="flex justify-end">
              <SortControl fields={SORT_FIELDS} sortBy={sortBy} sortDir={sortDir} onChange={setSort} testId={ORG_STAFF.sortControl} />
            </div>
          )}
          {staffQ.data && staffQ.data.length > 0 && (
            <div className="solid-surface rounded-[14px] overflow-hidden">
              <table className="w-full text-sm" data-testid={ORG_STAFF.table}>
                <thead>
                  <tr className="text-left" style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
                    <th className="eyebrow px-5 py-3">Name</th>
                    <th className="eyebrow px-5 py-3">Role</th>
                    <th className="eyebrow px-5 py-3">Branches</th>
                    <th className="eyebrow px-5 py-3">Status</th>
                    <th className="eyebrow px-5 py-3">MFA</th>
                    <th className="eyebrow px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {staffQ.data.map((s) => (
                    <tr key={s.id} data-testid={ORG_STAFF.row(s.id)} style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
                      <td className="px-5 py-3.5">
                        <p className="font-medium" style={{ color: "var(--scarlet-text)" }}>{s.name}</p>
                        <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>{s.email}</p>
                      </td>
                      <td className="px-5 py-3.5" style={{ color: "var(--scarlet-text-secondary)" }}>{roleLabelByName[s.role_name] || s.role_name}</td>
                      <td className="px-5 py-3.5 text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>{s.branch_scope.length ? s.branch_scope.map((b) => branchNameById[b] || b).join(", ") : "All branches"}</td>
                      <td className="px-5 py-3.5">
                        <StatusPill status={s.status === "active" ? "active" : "overdue"} label={s.status === "suspended" ? "Suspended" : s.status === "active" ? "Active" : "Inactive"} />
                        {s.status === "suspended" && s.suspension?.reason && (
                          <p className="text-xs mt-1" style={{ color: "var(--scarlet-text-secondary)" }}>"{s.suspension.reason}"</p>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>{s.mfa_enrolled ? "Enrolled" : "—"}</td>
                      <td className="px-5 py-3.5 text-right whitespace-nowrap">
                        <Button size="sm" variant="outline" data-testid={ORG_STAFF.editButton(s.id)} onClick={() => { setEditingStaff(s); setFormOpen(true); }} className="mr-2">Edit</Button>
                        {s.status === "active" ? (
                          <Button size="sm" variant="outline" data-testid={ORG_STAFF.suspendButton(s.id)} onClick={() => setSuspendTarget(s)} style={{ color: "var(--scarlet-neg)" }}>Suspend</Button>
                        ) : (
                          <Button size="sm" variant="outline" data-testid={ORG_STAFF.unsuspendButton(s.id)} onClick={() => unsuspendMutation.mutate(s.id)}>
                            {s.status === "suspended" ? "Unsuspend" : "Reactivate"}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "roles" && (
        <div className="grid sm:grid-cols-2 gap-4">
          {(rolesQ.data || []).map((r) => (
            <div key={r.name} data-testid={ORG_STAFF.roleCard(r.name)} className="solid-surface rounded-[14px] p-5">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck size={16} style={{ color: "var(--scarlet-accent)" }} />
                <p className="font-semibold" style={{ color: "var(--scarlet-text)" }}>{r.label}</p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {r.permissions.map((p) => (
                  <span key={p} className="text-xs rounded-full px-2 py-0.5" style={{ background: "var(--scarlet-hairline)", color: "var(--scarlet-text-secondary)" }}>{p === "*" ? "Everything" : p}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <StaffFormDialog open={formOpen} onOpenChange={setFormOpen} staff={editingStaff} roles={rolesQ.data || []} branches={branches} />
      <SuspendDialog
        open={!!suspendTarget}
        onOpenChange={(v) => !v && setSuspendTarget(null)}
        onConfirm={(reason) => suspendMutation.mutate({ id: suspendTarget.id, reason })}
        isPending={suspendMutation.isPending}
        entityLabel={suspendTarget?.name || "staff member"}
        testIds={ORG_STAFF}
      />
    </div>
  );
}
